import { useAuthStore, UserRole } from '@/store/authStore';

export type Feature =
  | 'timetable.view'
  | 'timetable.edit'
  | 'master-data.view'
  | 'master-data.edit'
  | 'absences.view'
  | 'absences.record'
  | 'reports.view'
  | 'import-export.use'
  | 'settings.view'
  | 'settings.edit';

// Feature access matrix based on user roles
const featureAccessMatrix: Record<Feature, UserRole[]> = {
  'timetable.view': ['admin', 'standard', 'teacher'],
  'timetable.edit': ['admin', 'standard'],
  'master-data.view': ['admin'],
  'master-data.edit': ['admin'],
  'absences.view': ['admin', 'standard'],
  'absences.record': ['admin', 'standard'],
  'reports.view': ['admin', 'standard'],
  'import-export.use': ['admin', 'standard'],
  'settings.view': ['admin'],
  'settings.edit': ['admin'],
};

export function useFeatureAccess() {
  const role = useAuthStore((state) => state.role);

  const hasAccess = (feature: Feature): boolean => {
    if (!role) return false;
    return featureAccessMatrix[feature]?.includes(role) ?? false;
  };

  const canViewTimetable = hasAccess('timetable.view');
  const canEditTimetable = hasAccess('timetable.edit');
  const canViewMasterData = hasAccess('master-data.view');
  const canEditMasterData = hasAccess('master-data.edit');
  const canViewAbsences = hasAccess('absences.view');
  const canRecordAbsences = hasAccess('absences.record');
  const canViewReports = hasAccess('reports.view');
  const canUseImportExport = hasAccess('import-export.use');
  const canViewSettings = hasAccess('settings.view');
  const canEditSettings = hasAccess('settings.edit');

  return {
    hasAccess,
    canViewTimetable,
    canEditTimetable,
    canViewMasterData,
    canEditMasterData,
    canViewAbsences,
    canRecordAbsences,
    canViewReports,
    canUseImportExport,
    canViewSettings,
    canEditSettings,
  };
}
